import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { formatWebhookMonitoringDateTime } from 'utils/webhook-monitoring.utils';
import { getDetailsModalFieldValueSx } from 'components/DetailsModal/DetailsLabelValue';

type Translate = (key: string) => string;

type Props = {
	attempts: any[];
	t: Translate;
};

const EMPTY_ATTEMPT_VALUE = '-';

export const DetailsAttemptsTable = memo(function DetailsAttemptsTable({ attempts, t }: Props) {
	const theme = useTheme();

	const headCellSx = useMemo(
		() => ({
			backgroundColor: theme.palette.root.brandSecondary1,
			color: theme.palette.root.basicDeeoGreen,
			fontWeight: 600,
			fontSize: theme.typography.caption?.fontSize,
			fontFamily: theme.typography.caption?.fontFamily,
			borderBottom: `1px solid ${theme.palette.root.inputDivider}`,
			whiteSpace: 'nowrap' as const,
		}),
		[theme]
	);

	const bodyCellBase = useMemo(
		() => ({
			...theme.typography.body1,
			...getDetailsModalFieldValueSx(theme),
			fontFamily: theme.typography.fontFamily,
			borderColor: theme.palette.root.inputDivider,
			transition: 'background-color 0.2s ease',
		}),
		[theme]
	);

	const rowHoverBg = useMemo(() => alpha(theme.palette.root.basicDeeoGreen, 0.05), [theme]);

	/** FormCard outer ~8px; table shell one step smaller for concentric corners. */
	const tableRadius = theme.spacing(0.75);

	return (
		<Box sx={{ WebkitFontSmoothing: 'antialiased' }}>
			{attempts.length === 0 ? (
				<Typography
					variant="body1"
					sx={{
						color: 'text.secondary',
						textWrap: 'pretty',
						textAlign: 'center',
						py: 2.5,
						px: 2,
						borderRadius: tableRadius,
						border: '1px dashed',
						borderColor: alpha(theme.palette.root.basicDeeoGreen, 0.25),
						bgcolor: alpha(theme.palette.root.brandSecondary1, 0.5),
					}}
				>
					{t('page.webhookMonitoring.attempts.empty')}
				</Typography>
			) : (
				<TableContainer
					sx={{
						borderRadius: tableRadius,
						overflow: 'hidden',
						width: '100%',
						border: 'none',
						boxShadow: `0 0 0 1px ${alpha(theme.palette.root.basicDeeoGreen, 0.12)}, 0 2px 12px ${alpha(
							theme.palette.common.black,
							0.06
						)}, 0 4px 20px ${alpha(theme.palette.common.black, 0.04)}`,
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.attemptNumber')}
								</TableCell>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.attemptedAt')}
								</TableCell>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.outcome')}
								</TableCell>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.httpStatusCode')}
								</TableCell>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.durationMs')}
								</TableCell>
								<TableCell sx={headCellSx}>
									{t('page.webhookMonitoring.attempt.field.errorMessage')}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{attempts.map((attempt: any, index: number) => (
								<TableRow
									key={attempt.id}
									sx={{
										'&:nth-of-type(even)': {
											backgroundColor: theme.palette.root.grayed,
										},
										'&:hover': {
											backgroundColor: rowHoverBg,
										},
									}}
								>
									<TableCell sx={{ ...bodyCellBase, fontVariantNumeric: 'tabular-nums' }}>
										{index + 1}
									</TableCell>
									<TableCell
										sx={{
											...bodyCellBase,
											whiteSpace: 'nowrap',
											fontVariantNumeric: 'tabular-nums',
										}}
									>
										{attempt.attemptedAt
											? formatWebhookMonitoringDateTime(attempt.attemptedAt)
											: EMPTY_ATTEMPT_VALUE}
									</TableCell>
									<TableCell sx={bodyCellBase}>
										{attempt.outcome
											? t(`page.webhookMonitoring.attempt.outcome.${attempt.outcome}`)
											: EMPTY_ATTEMPT_VALUE}
									</TableCell>
									<TableCell sx={{ ...bodyCellBase, fontVariantNumeric: 'tabular-nums' }}>
										{attempt.httpStatusCode ?? EMPTY_ATTEMPT_VALUE}
									</TableCell>
									<TableCell sx={{ ...bodyCellBase, fontVariantNumeric: 'tabular-nums' }}>
										{attempt.durationMs != null ? `${attempt.durationMs}ms` : EMPTY_ATTEMPT_VALUE}
									</TableCell>
									<TableCell
										sx={{
											...bodyCellBase,
											maxWidth: 200,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										<Tooltip title={attempt.errorMessage || ''} placement="top">
											<span>{attempt.errorMessage || EMPTY_ATTEMPT_VALUE}</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
});
